import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getBranches, createBranch, updateBranch, deleteBranch, getOrganization, getErrorMessage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { MapPin, Plus, Trash2, Pencil, Navigation } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export const BranchesPage = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [branches, setBranches] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    geo_fence_radius: 100
  });

  const fetchData = useCallback(async () => {
    try {
      const [branchesRes, orgRes] = await Promise.all([
        getBranches(),
        getOrganization()
      ]);
      setBranches(branchesRes.data);
      setOrganization(orgRes.data);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenCreate = () => {
    setEditMode(false);
    setSelectedBranch(null);
    setFormData({
      name: '',
      address: '',
      latitude: '',
      longitude: '',
      geo_fence_radius: 100
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (branch) => {
    setEditMode(true);
    setSelectedBranch(branch);
    setFormData({
      name: branch.name,
      address: branch.address,
      latitude: branch.latitude,
      longitude: branch.longitude,
      geo_fence_radius: branch.geo_fence_radius
    });
    setDialogOpen(true);
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          }));
          toast.success('Location detected');
        },
        (error) => {
          toast.error('Failed to get location');
        }
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      address: formData.address,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude),
      geo_fence_radius: parseFloat(formData.geo_fence_radius),
      org_id: organization?.id
    };

    try {
      if (editMode && selectedBranch) {
        await updateBranch(selectedBranch.id, data);
        toast.success('Branch updated successfully');
      } else {
        await createBranch(data);
        toast.success('Branch created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Operation failed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;
    try {
      await deleteBranch(id);
      toast.success('Branch deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete branch'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="branches-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const maxBranches = organization?.max_branches || 20;
  const isUnlimited = maxBranches === -1;
  const canCreate = isUnlimited || branches.length < maxBranches;

  return (
    <div className="space-y-8" data-testid="branches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
            Branches
          </h1>
          <p className="text-muted-foreground mt-1">
            {branches.length}{isUnlimited ? '' : ` of ${maxBranches}`} branches{isUnlimited ? '' : ' used'}
          </p>
        </div>
        
        {isAdmin && (
          <Button
            className="rounded-full"
            disabled={!canCreate}
            onClick={handleOpenCreate}
            data-testid="create-branch-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Branch
          </Button>
        )}
      </div>

      {/* Branches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.length === 0 ? (
          <Card className="col-span-full">
            <div className="empty-state py-12">
              <MapPin className="empty-state-icon" />
              <p className="text-muted-foreground">No branches yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first branch with geo-fencing
              </p>
            </div>
          </Card>
        ) : (
          branches.map((branch) => (
            <Card key={branch.id} data-testid={`branch-${branch.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{branch.name}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {branch.address}
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEdit(branch)}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid={`edit-branch-${branch.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(branch.id)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`delete-branch-${branch.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-4 p-3 rounded-lg bg-muted/50 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Geo-fence</span>
                    <span className="font-mono">{branch.geo_fence_radius}m radius</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Coordinates</span>
                    <span className="font-mono text-xs">
                      {branch.latitude.toFixed(4)}, {branch.longitude.toFixed(4)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editMode ? 'Edit Branch' : 'Create Branch'}
            </DialogTitle>
            <DialogDescription>
              {editMode ? 'Update branch details and geo-fence settings' : 'Add a new branch with geo-fencing'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Branch Name</Label>
              <Input
                placeholder="e.g., Main Office, Downtown Branch"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                data-testid="branch-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                placeholder="123 Main St, City, State"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                required
                data-testid="branch-address-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Location Coordinates</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGetCurrentLocation}
                  data-testid="get-location-btn"
                >
                  <Navigation className="w-3 h-3 mr-1" />
                  Use Current
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Latitude"
                  value={formData.latitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                  required
                  data-testid="branch-latitude-input"
                />
                <Input
                  placeholder="Longitude"
                  value={formData.longitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                  required
                  data-testid="branch-longitude-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Geo-fence Radius (meters)</Label>
              <Input
                type="number"
                min="10"
                max="10000"
                value={formData.geo_fence_radius}
                onChange={(e) => setFormData(prev => ({ ...prev, geo_fence_radius: e.target.value }))}
                required
                data-testid="branch-radius-input"
              />
              <p className="text-xs text-muted-foreground">
                Employees clocking in outside this radius will be flagged for review
              </p>
            </div>

            <Button type="submit" className="w-full" data-testid="submit-branch-btn">
              {editMode ? 'Update Branch' : 'Create Branch'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
